import React, {useCallback, useMemo, useRef, useState} from "react";
import PropTypes from 'prop-types';
import makeStyles from "@material-ui/core/styles/makeStyles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {TextField, Tooltip} from "@material-ui/core";
import Chip from "@material-ui/core/Chip";
import SortableContainer from "../../../../ReactSelectMaterial/Sortable/SortableContainer";
import {errorHandler} from "../../Utils/errorHandler";
import SortableItem from "../../../../ReactSelectMaterial/Sortable/SortableItem";
import {multiLanguageValue} from "../../../../../Components/JsonScreen/Utils/lib";
import {useSelector} from "react-redux";
import update from "immutability-helper";
import MultiLngMenu from "../../Utils/MultiLngMenu";
import {set} from "object-path-immutable";

const useStyles = makeStyles(() => ({
    inputRoot: {
        '&[class*="MuiInput-root"]': {
            paddingRight: 0,
            '& $input': {
                padding: '2px 4px',
            },
        },
        '&[class*="MuiOutlinedInput-root"]': {
            paddingRight: '0',
            padding: '1px'
        },
        '& .MuiInput-underline:before': {
            borderBottomColor: 'rgba(0,0,0,0.22)', // Semi-transparent underline
        },
    },
    option: {
        minHeight: 2,
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        cursor: 'pointer',
        paddingTop: 1,
        boxSizing: 'border-box',
        outline: '0',
        WebkitTapHighlightColor: 'transparent',
        paddingBottom: 2,
        paddingLeft: 16,
        paddingRight: 16,
    },
    textFieldRoot: {
        '& .MuiInput-underline:before': {
            borderBottomColor: 'rgba(0,0,0,0.22)', // Semi-transparent underline
        },
    },
    paper: {
        width: 'auto',
        '& > ul': {
            maxHeight: 200,
            overflow: 'auto',
        },
    },
    clearIndicator: {
        display: 'none',
    },
    tag: {
        height: '24px'
    },
}));

const ArrayField = (props) => {
    const {
        fieldValue, required, disabled, fieldRef, freeSolo,
        isMobile, label, fieldStyles, error, autofocus, pattern,
        type, path, field, minLength, maxLength, errors,
        fieldPresentation, placeholder, onChangeArray, multiLanguage, filterValue, onChangeFilterValue
    } = props;
    const chipFieldRef = useRef();
    const [orderedValue, setOrderedValue] = useState(null);
    const [editChip, setEditChip] = useState({
        value: "",
        index: null,
    })
    const [isOpenedMultiLngMenu, setIsOpenedMultiLngMenu] = useState(false);
    const classes = useStyles();
    const userLanguage = useSelector(state => state.app.userLanguage);
    const inputClassName = fieldStyles.withoutStyles ? 'horizontal-fields-table' : 'form-dynamic-default';
    const customStyles = (isMobile || fieldStyles.presentation === 'standard') ? {
            width: '100%',
            marginTop: 4
        } :
        {
            width: '100%',
            margin: fieldStyles.margin,
            paddingTop: fieldStyles.withoutPadding ? 0 : !isMobile && 3,
            paddingLeft: fieldStyles.withoutPadding ? 0 : !isMobile && 2,
        };

    const swapItems = (dragIndex, hoverIndex) => {
        let value = multiLanguage ? fieldValue[userLanguage] : fieldValue;
        let newArr = update(value, {
            $splice: [[dragIndex, 1], [hoverIndex, 0, fieldValue[dragIndex]]],
        });
        setOrderedValue(newArr)
        onChangeAutoComplete({}, newArr)
    };
    const endDrag = () => {
        if (orderedValue) {
            onChangeArray(orderedValue)
            setOrderedValue(null)
        }
    };

    const onChangeTextField = (event) => {
        const {value} = event.target;

        const arrValues = value.split(/\r?\n|\r|\n/g);

        if (arrValues.length > 1) {
            const v = arrValues.filter(v => v);
            onChangeArray([...fieldValue, ...v])
            return;
        }
        onChangeFilterValue(value)
    };
    const onChangeAutoComplete = (event, values) => {
        let newValue = values.map(v => {
            return typeof v === 'object' ? v.value : v
        });
        onChangeFilterValue("")
        if (multiLanguage) {
            newValue = {
                [userLanguage]: newValue
            }
        }
        onChangeArray(newValue)
    };

    const applyMultiLngMenu = (values) => {
        onChangeArray(values)
    }

    const onOpenMultiLngMenu = useCallback((isOpened) => {
        setIsOpenedMultiLngMenu(isOpened)
    }, [])

    const currentValue = useMemo(() => {
        if (multiLanguage) {
            const v = fieldValue[userLanguage] ? fieldValue[userLanguage] : fieldValue ? fieldValue : [];
            return addIndexes(v || [])
        }
        return addIndexes(fieldValue);
    }, [fieldValue, userLanguage, multiLanguage])

    function onBlur() {
        if (filterValue && !multiLanguage) {

            onChangeArray([...fieldValue, filterValue])

            onChangeFilterValue("")
        }
    }

    function onClickEditChip(index, value) {
        setEditChip({index, value})
        fieldRef.current.disabled = true;
    }

    function onChangeChipValue(e) {
        const {value} = e.target;

        setEditChip(prevState => {
            return {
                index: prevState.index,
                value
            }
        })
    }

    function onBlurChipValue() {
        setEditChip({
            index: null,
            value: "",
        })

        if (currentValue[editChip.index].value !== editChip.value) {
            const newValues = set(currentValue, `${editChip.index}.value`, editChip.value)
            onChangeArray(newValues.map(v => v.value))
        }
    }

    function onChipKeyDown(e) {
        e.stopPropagation();

        if (e.code === 'Escape') {
            setEditChip({
                index: null,
                value: ""
            })
            return;
        }

        if (e.code === 'Tab' || e.code === 'Enter') {
            onBlurChipValue();
        }

    }

    return (
        <SortableContainer>
            <Autocomplete
                value={currentValue || []}
                onChange={onChangeAutoComplete}
                disabled={disabled || isOpenedMultiLngMenu}
                ref={fieldRef}
                multiple
                open={false}
                options={[]}
                freeSolo={!freeSolo}
                classes={{
                    inputRoot: classes.inputRoot,
                    clearIndicator: classes.clearIndicator,
                    option: classes.option,
                    paper: classes.paper,
                    tag: classes.tag,
                }}
                className={"sb-entity-external-field"}
                inputValue={filterValue}
                onBlur={onBlur}
                renderInput={params =>
                    <TextField
                        {...params}
                        size={"small"}
                        placeholder={multiLanguageValue(placeholder, userLanguage)}
                        required={required && (fieldValue === null || !fieldValue.length)}
                        autoComplete="off"
                        label={(isMobile || fieldStyles.presentation === 'standard') && multiLanguageValue(label, userLanguage)}
                        fullWidth={isMobile ? true : fieldStyles.fullWidth}
                        className={inputClassName}
                        classes={{root: classes.textFieldRoot}}
                        error={error.length > 0}
                        inputProps={{
                            ...params.inputProps,
                            style: {
                                width: !isMobile && fieldStyles.width,
                                height: fieldStyles.height,
                                padding: '3px 0',
                                marginLeft: 2,
                            },
                            disabled: editChip.index !== null || params.inputProps.disabled,
                            pattern: pattern,
                            className: !fieldStyles.withoutStyles && 'with-styles',
                            minLength: minLength ? minLength : 1,
                            maxLength: maxLength ? maxLength : 1000
                        }}
                        InputProps={{
                            ...params.InputProps,
                            disabled: editChip.index !== null || params.InputProps.disabled,
                            endAdornment: multiLanguage && <MultiLngMenu value={fieldValue} apply={applyMultiLngMenu}
                                                                         isArray={true}
                                                                         onOpenCallback={onOpenMultiLngMenu}
                                                                         disabled={disabled}/>

                        }}
                        autoFocus={autofocus}
                        variant={isMobile ? 'standard' : fieldPresentation}
                        type={type}
                        onChange={onChangeTextField}
                        style={customStyles}
                        multiline={true}
                        rows={1}
                    />}
                renderTags={(value, getTagProps) =>
                    value.map((tag, index) => {
                        let error = errorHandler(errors, `${path}.${index}`);
                        return (
                            <SortableItem
                                index={index}
                                key={tag.idx}
                                moveCard={swapItems}
                                endDrag={endDrag}
                                element={field}
                                isDraggable={editChip.index !== index}
                            >
                                <Tooltip title={error.length > 0 ? error : ''}>
                                    <Chip
                                        id={`chip_${index}`}
                                        style={{
                                            border: error.length > 0 ? '1px solid red' : 'none',
                                            maxWidth: fieldStyles.width,
                                        }}
                                        onDoubleClick={() => onClickEditChip(index, tag.value)}
                                        variant="default"
                                        label={editChip.index === index ?
                                            <input value={editChip.value}
                                                   autoFocus={true}
                                                   className={"chip-input"}
                                                   ref={chipFieldRef}
                                                   onBlur={onBlurChipValue}
                                                   onChange={onChangeChipValue}
                                                   onKeyDown={onChipKeyDown}
                                                   onFocus={(e) => e.stopPropagation()}

                                            /> : tag.value}
                                        tabIndex={-1}
                                        {...getTagProps({index})}
                                    />
                                </Tooltip>
                            </SortableItem>
                        )
                    })
                }
            />
        </SortableContainer>
    )
};

export default ArrayField;

ArrayField.propTypes = {
    onChange: PropTypes.func,
    onChangeTextField: PropTypes.func,
    onBlur: PropTypes.func,
    fieldValue: PropTypes.array,
    required: PropTypes.bool,
    disabled: PropTypes.bool,
    fieldRef: PropTypes.any,
    freeSolo: PropTypes.bool,
    filterValue: PropTypes.string,
    isMobile: PropTypes.bool,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    fieldStyles: PropTypes.object,
    error: PropTypes.array,
    autofocus: PropTypes.bool,
    pattern: PropTypes.string,
    type: PropTypes.string,
    path: PropTypes.string,
    field: PropTypes.object,
    minLength: PropTypes.string,
    maxLength: PropTypes.string,
    errors: PropTypes.array,
    endDrag: PropTypes.func,
    swapItems: PropTypes.func,
};
ArrayField.defaultProps = {
    fieldPresentation: 'outlined'
}

function addIndexes(arr = []) {
    if (!Array.isArray(arr)) return [];
    return arr.map((v, i) => ({
        value: typeof v === 'object' ? v.value : v,
        idx: `${typeof v === 'object' ? v.value : v}_${i}`
    }))
}